// src/components/DateRangeSlider.jsx
import { Drawer, Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { es } from "date-fns/locale";
import { useEffect, useRef, useState } from "react";

const ACCENT = "#20AB8A";
const DARK = "#121826"; // azul oscuro elegante (base bichón)

export default function DateRangeSlider({
  open,
  onClose,
  startDate,
  endDate,
  onDatesChange,
  onDatesSelected,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [range, setRange] = useState([
    {
      startDate: startDate ? new Date(startDate + "T00:00:00") : new Date(),
      endDate: endDate ? new Date(endDate + "T00:00:00") : new Date(),
      key: "selection",
    },
  ]);

  const isStartSelected = useRef(false);

  const toISODate = (date) => date.toISOString().split("T")[0];

  const handleSelect = (ranges) => {
    const newRange = ranges.selection;
    const start = new Date(newRange.startDate);
    const end = new Date(newRange.endDate);

    // si toca mismo día, forzamos +1 día
    if (start.toDateString() === end.toDateString()) {
      const adjustedEnd = new Date(start);
      adjustedEnd.setDate(adjustedEnd.getDate() + 1);
      newRange.endDate = adjustedEnd;
    }

    setRange([newRange]);

    const isoStart = toISODate(newRange.startDate);
    const isoEnd = toISODate(newRange.endDate);

    onDatesChange?.({ startDate: isoStart, endDate: isoEnd });

    const daysSelected = Math.floor(
      (newRange.endDate - newRange.startDate) / (1000 * 60 * 60 * 24)
    );

    if (isStartSelected.current && daysSelected >= 1) {
      onDatesSelected?.({ startDate: isoStart, endDate: isoEnd });
      onClose?.();
      isStartSelected.current = false;
    } else {
      isStartSelected.current = true;
    }
  };

  useEffect(() => {
    setRange([
      {
        startDate: startDate ? new Date(startDate + "T00:00:00") : new Date(),
        endDate: endDate ? new Date(endDate + "T00:00:00") : new Date(),
        key: "selection",
      },
    ]);
  }, [startDate, endDate]);

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "top"}
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
        BackdropProps: {
          sx: {
            // overlay oscuro + blur (queda MUCHO más premium)
            backgroundColor: "rgba(10, 14, 23, 0.55)",
            backdropFilter: "blur(10px)",
          },
        },
      }}
      PaperProps={{
        sx: {
          // el Paper del drawer NO tiene que verse como bloque pesado
          bgcolor: "transparent",
          boxShadow: "none",
          overflow: "visible",
          borderBottomLeftRadius: isMobile ? 18 : 0,
          borderBottomRightRadius: isMobile ? 18 : 0,
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 2, md: 2.4 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Header (oscuro, prolijo, sin “bloque”) */}
        <Box
          sx={{
            width: "100%",
            maxWidth: 980,
            mb: 1.4,
            px: { xs: 1.4, sm: 2 },
            py: 1.1,
            borderRadius: 3,
            background: `linear-gradient(180deg, rgba(18,24,38,0.75), rgba(18,24,38,0.45))`,
            border: "1px solid rgba(255,255,255,0.12)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 900,
              textAlign: "center",
              color: "#fff",
              letterSpacing: 0.2,
            }}
          >
            Seleccioná entrada y salida
          </Typography>
          <Typography
            sx={{
              mt: 0.4,
              fontSize: 13,
              textAlign: "center",
              color: "rgba(255,255,255,0.72)",
            }}
          >
            Elija 2 fechas (mínimo 1 noche).
          </Typography>
        </Box>

        {/* Card blanca (la “protagonista”) */}
        <Box
          sx={{
            width: "100%",
            maxWidth: 980,
            borderRadius: 4,
            backgroundColor: "rgba(255,255,255,0.92)",
            border: "1px solid rgba(255,255,255,0.65)",
            boxShadow: "0 24px 70px rgba(0,0,0,0.35)",
            p: { xs: 1.2, sm: 1.6 },
          }}
        >
          <Box
            sx={{
              // “tema Bichón” para react-date-range
              "& .rdrCalendarWrapper": {
                background: "transparent",
                borderRadius: 16,
              },

              // barra de meses
              "& .rdrMonthAndYearWrapper": {
                paddingTop: 6,
              },
              "& .rdrMonthAndYearPickers select": {
                fontWeight: 800,
                color: DARK,
              },
              "& .rdrNextPrevButton": {
                background: "rgba(18,24,38,0.06)",
              },

              // textos generales
              "& .rdrWeekDay": { color: "rgba(18,24,38,0.55)", fontWeight: 700 },

              // hoy
              "& .rdrDayToday .rdrDayNumber span": {
                borderBottom: `2px solid ${ACCENT}`,
              },

              // números
              "& .rdrDayNumber span": {
                color: DARK,
                fontWeight: 700,
              },

              // días deshabilitados
              "& .rdrDayDisabled .rdrDayNumber span": {
                color: "rgba(18,24,38,0.25)",
              },

              // rango seleccionado
              "& .rdrInRange": {
                backgroundColor: "rgba(32,171,138,0.18) !important",
              },
              "& .rdrStartEdge, & .rdrEndEdge": {
                backgroundColor: `${ACCENT} !important`,
              },

              // preview al hover (queda fino)
              "& .rdrDayStartPreview, & .rdrDayEndPreview, & .rdrDayInPreview": {
                borderColor: `${ACCENT} !important`,
              },

              // evita que se vea “verde viejo” por default theme
              "& .rdrSelected, & .rdrDayStartOfMonth, & .rdrDayEndOfMonth": {
                boxShadow: "none",
              },
            }}
          >
            <DateRange
              ranges={range}
              onChange={handleSelect}
              minDate={new Date()}
              rangeColors={[ACCENT]}
              locale={es}
              showSelectionPreview
              moveRangeOnFirstSelection={false}
              editableDateInputs={false}
              months={isMobile ? 1 : 2}
              direction={isMobile ? "vertical" : "horizontal"}
            />
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
